/**
 * Asset Management Types
 * Types for managing generic assets (icons, images, videos, Lottie animations)
 */

// ============================================================================
// Enums
// ============================================================================

/**
 * Application types that can use assets
 * API returns string enum values
 */
export enum AssetApplicationType {
  EvHomeFlutter = 'EvHomeFlutter',
  EnergyAndroidCharger = 'EnergyAndroidCharger',
  EnergyAndroidKiosk = 'EnergyAndroidKiosk',
  WeezmoWeb = 'WeezmoWeb',
  WeezmoPdf = 'WeezmoPdf',
  FlutterWebApp = 'FlutterWebApp',
  ReactWeb = 'ReactWeb',
  General = 'General',
}

/**
 * Asset type categories with their constraints
 * API returns string enum values
 */
export enum AssetType {
  Icon = 'Icon',     // max 500KB - .png, .svg, .ico
  Image = 'Image',   // max 2MB - .jpg, .jpeg, .png, .gif, .webp
  Video = 'Video',   // max 50MB - .mp4, .webm
  Lottie = 'Lottie', // max 1MB - .json
  File = 'File',     // max 50MB - all file types
  Xml = 'Xml',       // max 1MB - .xml (requires screen size selection)
}

/**
 * Screen size density for XML assets — maps to the S3 path segment
 */
export enum ScreenSize {
  Small = 'mdpi',
  Medium = 'hdpi',
  Large = 'xhdpi',
}

export const SCREEN_SIZE_LABELS: Record<ScreenSize, string> = {
  [ScreenSize.Small]: 'Small (mdpi)',
  [ScreenSize.Medium]: 'Medium (hdpi)',
  [ScreenSize.Large]: 'Large (xhdpi)',
};

// ============================================================================
// Display Labels
// ============================================================================

export const APPLICATION_TYPE_LABELS: Record<AssetApplicationType, string> = {
  [AssetApplicationType.EvHomeFlutter]: 'EV Home Flutter',
  [AssetApplicationType.EnergyAndroidCharger]: 'Energy Android Charger',
  [AssetApplicationType.EnergyAndroidKiosk]: 'Energy Android Kiosk',
  [AssetApplicationType.WeezmoWeb]: 'Weezmo Web',
  [AssetApplicationType.WeezmoPdf]: 'Weezmo PDF',
  [AssetApplicationType.FlutterWebApp]: 'Flutter Web App',
  [AssetApplicationType.ReactWeb]: 'React Web',
  [AssetApplicationType.General]: 'General',
};

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  [AssetType.Icon]: 'Icon',
  [AssetType.Image]: 'Image',
  [AssetType.Video]: 'Video',
  [AssetType.Lottie]: 'Lottie Animation',
  [AssetType.File]: 'File',
  [AssetType.Xml]: 'XML File',
};

// ============================================================================
// Core Interfaces
// ============================================================================

/**
 * Asset item from the API
 */
export interface Asset {
  assetId: number;
  applicationType: AssetApplicationType;
  assetType: AssetType;
  name: string;
  description: string | null;
  currentVersion: number;
  currentFileName: string;
  currentS3Url: string;
  contentType: string;
  fileSizeBytes: number;
  machineId?: number | null; // Optional - asset belongs to specific machine
  created: string;
  createdByName: string | null;
  modified: string;
  modifiedByName: string | null;
}

/**
 * Asset version from the API
 */
export interface AssetVersion {
  versionId: number;
  versionNumber: number;
  fileName: string;
  originalFileName: string;
  s3Url: string;
  contentType: string;
  fileSizeBytes: number;
  created: string;
  createdByName: string | null;
}

/**
 * Asset with version history
 */
export interface AssetWithVersions {
  asset: Asset;
  versions: AssetVersion[];
}

// ============================================================================
// Request Types
// ============================================================================

/**
 * Parameters for listing assets
 */
export interface AssetListParams {
  applicationType?: AssetApplicationType;
  assetType?: AssetType;
  operatorId?: number; // Filter by operator (for distributors)
  machineId?: number; // Filter by machine ID
  search?: string;
  skip?: number;
  take?: number;
}

/**
 * Request to upload a new asset or new version
 */
export interface UploadAssetRequest {
  applicationType: AssetApplicationType;
  assetType: AssetType;
  operatorId: number;
  machineId?: number; // Optional - assign to specific machine
  name: string;
  description?: string;
  fileName: string;
  fileContent: string; // base64 encoded
  contentType?: string; // optional, auto-detected
  environment: number; // 1 = QA, 2 = Production
  screenSize?: ScreenSize; // required when assetType === Xml
}

/**
 * Form data for upload dialog - operatorId is optional
 * (will use current operator if not specified, or allow selection for distributors)
 */
export interface AssetUploadFormData {
  applicationType: AssetApplicationType;
  assetType: AssetType;
  operatorId?: number; // Optional - distributors can select, others use current operator
  machineId?: number; // Optional - assign to specific machine
  name: string;
  description?: string;
  fileName: string;
  fileContent: string;
  contentType?: string;
  screenSize?: ScreenSize; // required when assetType === Xml
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * Response for asset list operations
 */
export interface AssetListResponse {
  isSuccess: boolean;
  items: Asset[];
  totalCount: number;
  errorMessage?: string;
}

/**
 * Response for asset detail operations
 */
export interface AssetDetailResponse {
  isSuccess: boolean;
  asset: Asset;
  versions: AssetVersion[];
  errorMessage?: string;
}

/**
 * Response for upload operations
 */
export interface UploadAssetResponse {
  isSuccess: boolean;
  asset?: Asset;
  version?: AssetVersion;
  isNewAsset: boolean;
  errorMessage?: string;
}

/**
 * Response for delete operations
 */
export interface DeleteAssetResponse {
  isSuccess: boolean;
  errorMessage?: string;
}

/**
 * Response for rollback operations
 */
export interface RollbackAssetResponse {
  isSuccess: boolean;
  asset?: Asset;
  version?: AssetVersion;
  errorMessage?: string;
}

// ============================================================================
// Store Types
// ============================================================================

/**
 * Asset management store state and actions
 */
export interface AssetManagementStore {
  // Data
  assets: Asset[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  selectedAsset: AssetWithVersions | null;

  // Filters
  applicationTypeFilter: AssetApplicationType | null;
  assetTypeFilter: AssetType | null;
  searchQuery: string;

  // UI States
  isLoading: boolean;
  isLoadingDetail: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  isRollingBack: boolean;
  error: string | null;
  uploadError: string | null;

  // Dialog state
  uploadDialogOpen: boolean;
  detailDialogOpen: boolean;
  deleteDialogOpen: boolean;
  assetToDelete: Asset | null;

  // Actions
  fetchAssets: () => Promise<void>;
  fetchAssetDetail: (assetId: number) => Promise<void>;
  uploadAsset: (request: AssetUploadFormData) => Promise<Asset | null>;
  deleteAsset: (assetId: number) => Promise<boolean>;
  rollbackAsset: (assetId: number, versionId: number) => Promise<boolean>;
  setApplicationTypeFilter: (appType: AssetApplicationType | null) => void;
  setAssetTypeFilter: (assetType: AssetType | null) => void;
  setSearchQuery: (query: string) => void;
  setCurrentPage: (page: number) => void;
  openUploadDialog: () => void;
  closeUploadDialog: () => void;
  openDetailDialog: (assetId: number) => Promise<void>;
  closeDetailDialog: () => void;
  openDeleteDialog: (asset: Asset) => void;
  closeDeleteDialog: () => void;
  clearUploadError: () => void;
  reset: () => void;
}

// ============================================================================
// File Validation Constants
// ============================================================================

/**
 * File size limits per asset type (in bytes)
 */
export const ASSET_SIZE_LIMITS: Record<AssetType, number> = {
  [AssetType.Icon]: 500 * 1024,        // 500KB
  [AssetType.Image]: 2 * 1024 * 1024,   // 2MB
  [AssetType.Video]: 50 * 1024 * 1024,  // 50MB
  [AssetType.Lottie]: 1 * 1024 * 1024,  // 1MB
  [AssetType.File]: 50 * 1024 * 1024,   // 50MB
  [AssetType.Xml]: 1 * 1024 * 1024,     // 1MB
};

/**
 * Allowed file extensions per asset type
 */
export const ASSET_ALLOWED_EXTENSIONS: Record<AssetType, string[]> = {
  [AssetType.Icon]: ['.png', '.svg', '.ico'],
  [AssetType.Image]: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  [AssetType.Video]: ['.mp4', '.webm'],
  [AssetType.Lottie]: ['.json'],
  [AssetType.File]: [], // all extensions allowed
  [AssetType.Xml]: ['.xml'],
};

/**
 * Allowed MIME types per asset type
 */
export const ASSET_ALLOWED_MIME_TYPES: Record<AssetType, string[]> = {
  [AssetType.Icon]: ['image/png', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'],
  [AssetType.Image]: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  [AssetType.Video]: ['video/mp4', 'video/webm'],
  [AssetType.Lottie]: ['application/json'],
  [AssetType.File]: [], // all MIME types allowed
  [AssetType.Xml]: ['application/xml', 'text/xml'],
};

/**
 * Default page size for assets list
 */
export const DEFAULT_ASSETS_PAGE_SIZE = 50;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? '.' + parts.pop()!.toLowerCase() : '';
}

/**
 * Validate if file is allowed for the given asset type
 */
export function isValidAssetFile(file: File, assetType: AssetType): { valid: boolean; error?: string } {
  const maxSize = ASSET_SIZE_LIMITS[assetType];

  // File type accepts all extensions - only check size
  if (assetType !== AssetType.File) {
    const extension = getFileExtension(file.name);
    const allowedExtensions = ASSET_ALLOWED_EXTENSIONS[assetType];
    if (!allowedExtensions.includes(extension)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed: ${allowedExtensions.join(', ')}`,
      };
    }
  }

  // Check size
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${formatFileSize(maxSize)}`,
    };
  }

  return { valid: true };
}

/**
 * Get accept string for file input
 */
export function getAcceptString(assetType: AssetType): string {
  if (assetType === AssetType.File) return '*/*';
  const extensions = ASSET_ALLOWED_EXTENSIONS[assetType];
  const mimeTypes = ASSET_ALLOWED_MIME_TYPES[assetType];
  return [...extensions, ...mimeTypes].join(',');
}

/**
 * Convert a File to base64 string (without data URL prefix)
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Check if asset type supports inline preview
 */
export function canPreviewInline(assetType: AssetType): boolean {
  return assetType === AssetType.Icon || assetType === AssetType.Image || assetType === AssetType.Lottie;
}

export function isFileAsset(assetType: AssetType): boolean {
  return assetType === AssetType.File;
}

/**
 * Check if asset type is a video
 */
export function isVideoAsset(assetType: AssetType): boolean {
  return assetType === AssetType.Video;
}

/**
 * Check if asset type is a Lottie animation
 */
export function isLottieAsset(assetType: AssetType): boolean {
  return assetType === AssetType.Lottie;
}

/**
 * Format date-time for display
 * Matches project standard: DD/MM/YYYY HH:MM:SS (en-GB)
 */
export function formatDate(dateString: string): string {
  if (!dateString) {
    return '--';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '--';
    }

    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '--';
  }
}

/**
 * Get application type options for dropdown
 */
export function getApplicationTypeOptions(): { value: AssetApplicationType; label: string }[] {
  return Object.entries(APPLICATION_TYPE_LABELS).map(([value, label]) => ({
    value: value as AssetApplicationType,
    label,
  }));
}

/**
 * Get asset type options for dropdown
 */
export function getAssetTypeOptions(): { value: AssetType; label: string }[] {
  return Object.entries(ASSET_TYPE_LABELS).map(([value, label]) => ({
    value: value as AssetType,
    label,
  }));
}
